import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const TOKEN_KEY = 'zsp.token';

type AuthState = {
  token: string | null;
  setToken: (token: string | null) => void;
};

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const queryClient = useQueryClient();

  const setToken = useCallback(
    (t: string | null) => {
      if (t) localStorage.setItem(TOKEN_KEY, t);
      else localStorage.removeItem(TOKEN_KEY);
      setTokenState(t);
      // Any cached data belongs to the previous identity — drop it so the next
      // user doesn't see stale `me`/list results until a background refetch lands.
      queryClient.clear();
    },
    [queryClient],
  );

  const value = useMemo(() => ({ token, setToken }), [token, setToken]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Drop the stored token (used by the global 401 interceptor on an expired /
 *  invalid session, where there's no React context handy). */
export function clearAuthToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}
